// src/components/register/hooks/useRegister.js

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../services/registerService';
import { buildRegisterUserPayload } from '../utils/buildRegisterUserPayload';
import { compressAllImages } from '../utils/compressImage';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB límite inicial por archivo

export const useRegister = () => {
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState(1);

    // El estado inicial para archivos es mejor que sea null en lugar de string vacío
    const [formData, setFormData] = useState({
        commerceName: '',
        customerName: '',
        lastName: '',
        lastName2: '',
        email: '',
        password: '',
        confirmPassword: '',
        address: '',
        phone: '',
        ine: null, 
        face: null,
        proofOfAddress: null,
        ineback: null,
        clabe: null
    });

    const [loading, setLoading] = useState(false);
    const [loadingLogin, setLoadingLogin] = useState(false); // Transición a Inicio de sesión
    const [message, setMessage] = useState('');
    const [error, setError] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === 'file') {
            const file = files[0];
            if (file) {
                const isHeic = (
                    file.type === 'image/heic' ||
                    file.type === 'image/heif' ||
                    file.name.toLowerCase().endsWith('.heic') ||
                    file.name.toLowerCase().endsWith('.heif')
                );

                if (isHeic) {
                    setError(true);
                    setMessage('El formato .HEIC de Apple no es compatible. Por favor adjunta una foto en formato JPG, PNG o WEBP.');
                    e.target.value = '';
                    setFormData((prevData) => ({
                        ...prevData,
                        [name]: null
                    }));
                    return;
                }

                if (!file.type.startsWith('image/')) {
                    setError(true);
                    setMessage('Solo se permiten archivos de imagen (JPG, PNG, JPEG, WEBP).');
                    e.target.value = '';
                    setFormData((prevData) => ({
                        ...prevData,
                        [name]: null
                    }));
                    return;
                }

                if (file.size > MAX_FILE_SIZE_BYTES) {
                    setError(true);
                    setMessage(`El archivo "${file.name}" supera el tamaño máximo permitido de 10 MB.`);
                    e.target.value = '';
                    setFormData((prevData) => ({
                        ...prevData,
                        [name]: null
                    }));
                    return;
                }
            }
        }

        // Teléfono: solo dígitos y máximo 10 caracteres
        if (name === 'phone') {
            const digits = value.replace(/\D/g, '').slice(0, 10);
            setFormData((prevData) => ({ ...prevData, phone: digits }));
            return;
        }

        const inputValue = type === 'file' ? files[0] : value;

        setFormData((prevData) => ({
            ...prevData,
            [name]: inputValue
        }));
    };

    const nextStep = (e) => {
        if (e) e.preventDefault();
        setMessage('');
        setError(false);

        // Validación de campos del Paso 1
        if (
            !formData.commerceName ||
            !formData.customerName ||
            !formData.lastName ||
            !formData.lastName2 ||
            !formData.email ||
            !formData.password ||
            !formData.confirmPassword ||
            !formData.address ||
            !formData.phone
        ) {
            setError(true);
            setMessage('Por favor completa todos los campos obligatorios del Paso 1.');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError(true);
            setMessage('Por favor introduce un correo electrónico válido.');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError(true);
            setMessage('Las contraseñas no coinciden.');
            return;
        }

        if (formData.password.length < 8) {
            setError(true);
            setMessage('La contraseña debe tener al menos 8 caracteres.');
            return;
        }

        if (!/[A-Z]/.test(formData.password)) {
            setError(true);
            setMessage('La contraseña debe contener al menos una letra mayúscula.');
            return;
        }

        if (!/[0-9]/.test(formData.password)) {
            setError(true);
            setMessage('La contraseña debe contener al menos un número.');
            return;
        }

        setCurrentStep(2);
    };

    const prevStep = () => {
        setMessage('');
        setError(false);
        setCurrentStep(1);
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        setMessage('');
        setError(false);

        if (!formData.ine || !formData.ineback || !formData.face || !formData.proofOfAddress || !formData.clabe) {
            setError(true);
            setMessage('Por favor adjunta todos los documentos requeridos.');
            return;
        }

        const filesToCheck = [formData.ine, formData.ineback, formData.face, formData.proofOfAddress, formData.clabe];

        const hasHeic = filesToCheck.some(file => file && (
            file.type === 'image/heic' ||
            file.type === 'image/heif' ||
            file.name.toLowerCase().endsWith('.heic') ||
            file.name.toLowerCase().endsWith('.heif')
        ));
        if (hasHeic) {
            setError(true);
            setMessage('Uno o más archivos están en formato .HEIC de Apple. Por favor cámbialos a JPG, PNG o WEBP.');
            return;
        }

        const hasNonImage = filesToCheck.some(file => file && !file.type.startsWith('image/'));
        if (hasNonImage) {
            setError(true);
            setMessage('Todos los documentos adjuntados deben ser imágenes válidas (JPG, PNG, WEBP).');
            return;
        }

        const hasOversized = filesToCheck.some(file => file && file.size > MAX_FILE_SIZE_BYTES);
        if (hasOversized) {
            setError(true);
            setMessage('Uno o más documentos superan el límite de 10 MB por archivo.');
            return;
        }

        // Abre el modal de términos y condiciones para confirmación
        setShowTermsModal(true);
    };

    const handleConfirmRegister = async () => {
        if (!acceptedTerms) {
            return;
        }

        setShowTermsModal(false);
        setLoading(true);
        setMessage('');
        setError(false);

        try {
            // Comprime las imágenes manteniendo su formato original antes de enviarlas al servidor
            const compressedImages = await compressAllImages({
                ine: formData.ine,
                ineback: formData.ineback,
                face: formData.face,
                proofOfAddress: formData.proofOfAddress,
                clabe: formData.clabe
            });

            const payload = buildRegisterUserPayload({
                ...formData,
                ...compressedImages
            });
            
            const data = await registerUser(payload);

            setShowWelcomeModal(true);
            
            setTimeout(() => {
                setShowWelcomeModal(false);
                navigate('/Inicio');
            }, 3000);

        } catch (err) {
            setError(true);
            const rawMsg = err.message || '';
            if (
                rawMsg.includes('Load failed') ||
                rawMsg.includes('Failed to fetch') ||
                rawMsg.includes('Network Error') ||
                rawMsg.includes('ERR_NETWORK')
            ) {
                setMessage('Error de conexión con el servidor. Verifica tu conexión a internet o que los archivos no sean excesivamente pesados.');
            } else {
                setMessage(rawMsg || 'Error al conectar con el servidor.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCloseTermsModal = () => {
        setShowTermsModal(false);
        setAcceptedTerms(false);
    };

    const handleGoToLogin = (e) => {
        if (e) e.preventDefault();
        setLoadingLogin(true);
        setTimeout(() => {
            navigate('/Inicio');
        }, 700);
    };

    return {
        formData,
        currentStep,
        nextStep,
        prevStep,
        loading,
        loadingLogin,
        message,
        error,
        showWelcomeModal,
        showTermsModal,
        acceptedTerms,
        setAcceptedTerms,
        handleConfirmRegister,
        handleCloseTermsModal,
        handleGoToLogin,
        handleChange,
        handleSubmit
    };
};